import React, { useState } from "react";
import { Card } from "../card";
import { BtnSize, BtnType, Button } from "../Button";
import { RootState } from "../../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  reset,
  setEmail,
  setFirstName,
  setGender,
  setLastName,
  setMiddleName,
  setMobile,
  setParentalRelationship,
  setAddress,
  setPassword,
} from "../../Redux/Slices/registerParents.slice";
import { useParentRegistration } from "../../hooks/parentsHooks/useParents.hooks";
import {
  setErrorMessage,
  setSuccessMessage,
} from "../../Redux/Slices/alert.slice";
import { verifyParentsRegistrationFields } from "../../Helper/validation/parentsRegistration.validation.helper";
import { SOMETHING_WENT_WRONG } from "../../Constants/messages";
import queryClient from "../../config/queryClient";
import { PARENTS_LIST_KEYS } from "../../hooks/parentsHooks/useParentsList.hooks";
import { SectionHeader } from "../Header/SectionHeader";
import { ParentDetails } from "./ParentDetails";
import { ParentAccountDetails } from "./ParentAccountDetails";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../Constants/paths";

const ParentsRegistrationForm = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const {
    firstName,
    middleName,
    lastName,
    gender,
    parentalRelationship,
    email,
    password,
    mobile,
    address,
  } = useSelector((state: RootState) => ({
    firstName: state.registerParents.firstName,
    middleName: state.registerParents.middleName,
    lastName: state.registerParents.lastName,
    gender: state.registerParents.gender,
    email: state.registerParents.email,
    password: state.registerParents.password,
    mobile: state.registerParents.mobile,
    parentalRelationship: state.registerParents.parentalRelationship,
    address: state.registerParents.address,
  }));

  //Dont need confirmed password to persist in localstorage thats why local state is used.
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const { isLoading: isRegisteringLoading, registerParents } =
    useParentRegistration();

  const verifyFields = () => {
    return verifyParentsRegistrationFields({
      fieldsToVerify: {
        firstName,
        lastName,
        gender,
        email,
        password,
        confirmPassword,
        mobile,
        parentalRelationship,
        address,
      },
      dispatch,
    });
  };

  const onRegisterParents = () => {
    const isValid = verifyFields();
    if (!isValid) return;
    registerParents(
      {
        firstName,
        middleName,
        lastName,
        gender,
        email,
        password,
        mobile,
        parentalRelationship,
        address,
      },
      {
        onSuccess: () => {
          dispatch(
            setSuccessMessage("Parents has been registered successfully.")
          );
          setConfirmPassword("");

          dispatch(reset());

          queryClient.invalidateQueries({
            queryKey: [PARENTS_LIST_KEYS.PARENTS_LIST],
          });

          navigate(Paths.ParentsList);
        },

        onError: (error: any) => {
          dispatch(
            setErrorMessage(error.response.data.message || SOMETHING_WENT_WRONG)
          );
        },
      }
    );
  };

  return (
    <Card
      cardTitle="Register new parents"
      cardSubTitle="Streamline the onboarding process by registering new parents effortlessly. Simplify data entry, manage profiles, and enhance the parent registration experience with ease."
    >
      <SectionHeader headerText="General information" />
      <ParentDetails
        firstName={firstName}
        middleName={middleName}
        lastName={lastName}
        mobile={mobile}
        address={address}
        gender={gender}
        parentalRelationship={parentalRelationship}
        onFirstNameChange={(firstName) => {
          dispatch(setFirstName(firstName));
        }}
        onMiddleNameChange={(middleName) => {
          dispatch(setMiddleName(middleName));
        }}
        onlastNameChange={(lastName) => {
          dispatch(setLastName(lastName));
        }}
        onMobileChange={(mobile) => {
          dispatch(setMobile(mobile));
        }}
        onAddressChange={(address) => {
          dispatch(setAddress(address));
        }}
        onGenderChange={(gender) => {
          dispatch(setGender(gender));
        }}
        onParentalRelationShipChange={(parentalRelationship) => {
          dispatch(setParentalRelationship(parentalRelationship));
        }}
      />
      <SectionHeader
        headerText="Account information"
        subText="Enter precise account details for parents during registration. This information will serve as their login credentials for secure access to the system."
      />
      <ParentAccountDetails
        email={email}
        password={password}
        confirmPassword={confirmPassword}
        onEmailChange={(email) => dispatch(setEmail(email))}
        onPasswordChange={(password) => dispatch(setPassword(password))}
        onConfirmPasswordChange={(confirmPassword) =>
          setConfirmPassword(confirmPassword)
        }
      />
      <div className="flex flex-col lg:flex-row md:flex-row gap-y-2 md:gap-y-0 lg:gap-y-0 gap-x-5 w-full mt-5">
        <Button
          text="Reset"
          loading={false}
          btnSize={BtnSize.md}
          btnType={BtnType.basic}
          onClick={() => {
            setConfirmPassword("");
            dispatch(reset());
          }}
        />
        <Button
          text="Register"
          loading={isRegisteringLoading}
          btnSize={BtnSize.md}
          onClick={onRegisterParents}
          requiresValidationOnSubmit
        />
      </div>
    </Card>
  );
};
export default ParentsRegistrationForm;
